import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Spin,
  Typography,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet-async";

import CanvasArea, { CANVAS_HEIGHT, CANVAS_WIDTH } from "./CanvasArea";
import { API_BASE_URL } from "../../utils/constants";
import "../../assets/styles/components_styles/InvitationSharePreview.css";

const { Content } = Layout;

function computeContainScale(width, height) {
  if (width <= 0 || height <= 0) return 1;
  return Math.min(width / CANVAS_WIDTH, height / CANVAS_HEIGHT, 1);
}

export default function InvitationSharePreviewPage() {
  const { code } = useParams();
  const [form] = Form.useForm();
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [rsvpModalOpen, setRsvpModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scale, setScale] = useState(() =>
    typeof window !== "undefined"
      ? computeContainScale(window.innerWidth - 40, window.innerHeight - 120)
      : 1
  );

  const recomputeScale = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    setScale(computeContainScale(w, h));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    axios
      .get(
        `${API_BASE_URL}/public/invitation-preview/${encodeURIComponent(code)}`
      )
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (!data?.canvas_data) {
          setError("Invalid invitation data");
          return;
        }
        setPreview(data);
      })
      .catch((e) => {
        if (cancelled) return;
        const detail = e.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : "This invitation could not be found."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (loading || error || !preview?.canvas_data || !code) return;
    let cancelled = false;
    (async () => {
      try {
        const { default: FingerprintJS } = await import(
          "@fingerprintjs/fingerprintjs"
        );
        const fp = await FingerprintJS.load({ monitoring: false });
        const { visitorId } = await fp.get();
        if (cancelled || !visitorId) return;
        await axios.post(
          `${API_BASE_URL}/public/invitation-preview/${encodeURIComponent(code)}/visit`,
          { visitor_fingerprint: visitorId }
        );
      } catch (e) {
        console.warn("Invitation preview visit log failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, loading, error, preview]);

  useEffect(() => {
    if (!canvasReady || !preview?.canvas_data) return;
    let cancelled = false;
    (async () => {
      try {
        await canvasRef.current?.loadFromJson(preview.canvas_data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError("Could not render this invitation.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canvasReady, preview]);

  useLayoutEffect(() => {
    if (!preview || loading || error) return undefined;

    const el = viewportRef.current;
    if (!el) return undefined;

    recomputeScale();
    const ro = new ResizeObserver(() => recomputeScale());
    ro.observe(el);

    const vv = window.visualViewport;
    const onViewportChange = () => recomputeScale();
    vv?.addEventListener("resize", onViewportChange);
    window.addEventListener("resize", onViewportChange);

    return () => {
      ro.disconnect();
      vv?.removeEventListener("resize", onViewportChange);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [preview, loading, error, recomputeScale]);

  const handleCanvasReady = () => setCanvasReady(true);

  const closeRsvpModal = useCallback(() => {
    setRsvpModalOpen(false);
    form.resetFields();
  }, [form]);

  const contactEmailOrPhoneRule = {
    validator: async () => {
      const email = form.getFieldValue("email");
      const phone = form.getFieldValue("phone");
      const e = typeof email === "string" ? email.trim() : "";
      const p = typeof phone === "string" ? phone.trim() : "";
      if (!e && !p) {
        return Promise.reject(
          new Error("Please enter an email or phone number")
        );
      }
      return Promise.resolve();
    },
  };

  const handleRsvpSubmit = async (values) => {
    setRsvpSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/public/invitation-preview/${encodeURIComponent(code)}/rsvp`,
        {
          name: values.name.trim(),
          email: values.email?.trim() || null,
          phone: values.phone?.trim() || null,
          guest_count: values.guest_count ?? 1,
        }
      );
      message.success("Thanks — your RSVP has been saved.");
      closeRsvpModal();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Could not save your RSVP. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        if (typeof first === "string") msg = first;
        else if (first?.msg) msg = first.msg;
      }
      message.error(msg);
    } finally {
      setRsvpSubmitting(false);
    }
  };

  const rawName =
    preview?.name != null && String(preview.name).trim()
      ? String(preview.name).trim()
      : "";
  const shareTitle = rawName ? `${rawName} - PingVite` : "Invitation - PingVite";

  const shareLoaded = Boolean(!loading && !error && preview);
  const canonicalUrl =
    typeof window !== "undefined" && shareLoaded && code
      ? `${window.location.origin}/invitation-share/${encodeURIComponent(code)}`
      : undefined;
  const ogImage = shareLoaded && preview?.thumbnail_url ? preview.thumbnail_url : undefined;
  const twitterCard = ogImage ? "summary_large_image" : "summary";

  const scaledW = CANVAS_WIDTH * scale;
  const scaledH = CANVAS_HEIGHT * scale;

  return (
    <Layout className="invitation-share-preview">
      <Helmet>
        <title>{shareTitle}</title>
        {shareLoaded && (
          <>
            <meta property="og:title" content={shareTitle} />
            <meta property="og:site_name" content="PingVite" />
            <meta property="og:type" content="website" />
            {canonicalUrl ? (
              <meta property="og:url" content={canonicalUrl} />
            ) : null}
            {ogImage ? <meta property="og:image" content={ogImage} /> : null}
            <meta
              property="og:description"
              content="View this invitation on PingVite"
            />
            <meta name="twitter:card" content={twitterCard} />
            <meta name="twitter:title" content={shareTitle} />
            {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
          </>
        )}
      </Helmet>
      <Content>
        <div className="invitation-share-preview__inner">
          <div className="invitation-share-preview__top">
            <Link to="/" className="invitation-share-preview__back">
              <ArrowLeftOutlined /> <span>Home</span>
            </Link>
            {preview?.name ? (
              <Typography.Title
                level={4}
                className="invitation-share-preview__title"
                ellipsis
              >
                {preview.name}
              </Typography.Title>
            ) : (
              <span />
            )}
          </div>

          {loading && (
            <div className="invitation-share-preview__loading">
              <Spin size="large" tip="Loading invitation…" />
            </div>
          )}

          {!loading && error && (
            <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
          )}

          {!loading && !error && preview && (
            <>
              <div className="invitation-share-preview__rsvp-bar">
                <Typography.Text className="invitation-share-preview__rsvp-bar-text">
                  Let the host know you&apos;re coming.
                </Typography.Text>
                <Button type="primary" onClick={() => setRsvpModalOpen(true)}>
                  RSVP
                </Button>
              </div>
              <Modal
                title="RSVP"
                open={rsvpModalOpen}
                onCancel={closeRsvpModal}
                closable
                maskClosable
                keyboard
                footer={null}
                destroyOnClose
                className="invitation-share-preview__rsvp-modal"
                width={440}
              >
                <Form
                  form={form}
                  layout="vertical"
                  className="invitation-share-preview__rsvp-form"
                  onFinish={handleRsvpSubmit}
                  requiredMark="optional"
                  initialValues={{ guest_count: 1 }}
                >
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter your name" }]}
                  >
                    <Input autoComplete="name" placeholder="Your name" />
                  </Form.Item>
                  <Form.Item
                    label="Email"
                    name="email"
                    dependencies={["phone"]}
                    rules={[contactEmailOrPhoneRule]}
                  >
                    <Input type="email" autoComplete="email" placeholder="you@example.com" />
                  </Form.Item>
                  <Form.Item
                    label="Phone"
                    name="phone"
                    dependencies={["email"]}
                    rules={[contactEmailOrPhoneRule]}
                  >
                    <Input type="tel" autoComplete="tel" placeholder="Phone number" />
                  </Form.Item>
                  <Form.Item
                    label={
                      <span className="invitation-share-preview__rsvp-guest-count-label">
                        <span>Number of guests</span>
                        <Typography.Text
                          type="secondary"
                          className="invitation-share-preview__rsvp-guest-count-hint"
                        >
                          Helps us manage better
                        </Typography.Text>
                      </span>
                    }
                    name="guest_count"
                    rules={[
                      { required: true, message: "Please enter number of guests" },
                      {
                        type: "number",
                        min: 1,
                        max: 20,
                        message: "Enter a number between 1 and 20",
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={20}
                      precision={0}
                      className="invitation-share-preview__rsvp-guest-count-input"
                    />
                  </Form.Item>
                  <Typography.Paragraph type="secondary" className="invitation-share-preview__rsvp-hint">
                    Email or phone (or both) is required so we can reach you.
                  </Typography.Paragraph>
                  <Form.Item className="invitation-share-preview__rsvp-form-actions">
                    <div className="invitation-share-preview__rsvp-modal-footer-btns">
                      <Button onClick={closeRsvpModal}>Cancel</Button>
                      <Button type="primary" htmlType="submit" loading={rsvpSubmitting}>
                        Submit RSVP
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              </Modal>
              <div
                ref={viewportRef}
                className="invitation-share-preview__viewport"
              >
                <div
                  className="invitation-share-preview__canvas-scale-outer"
                  style={{
                    width: scaledW,
                    height: scaledH,
                  }}
                >
                  <div
                    className="invitation-share-preview__canvas-scale-inner"
                    style={{
                      width: CANVAS_WIDTH,
                      height: CANVAS_HEIGHT,
                      transform: `scale(${scale})`,
                    }}
                  >
                    <CanvasArea
                      key={code}
                      readOnly
                      ref={canvasRef}
                      onReady={handleCanvasReady}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Content>
    </Layout>
  );
}
